/**
 * Timer for the Web Audio lookahead schedulers.
 *
 * The schedulers refill a short window (100–250 ms) of the audio clock every
 * 25 ms. Main-thread setInterval is the wrong clock for that: browsers clamp
 * it to 1 s in background tabs (and further under intensive throttling), which
 * starves the window and drops notes, and a long React commit delays it just
 * the same. A dedicated worker's timer is not throttled that way, so one
 * shared worker posts ticks and the main thread runs every due callback.
 *
 * The callbacks still run on the main thread — the audio graph lives there —
 * but they fire on time whenever the thread is free, instead of whenever the
 * tab is visible. Environments without Worker (tests, SSR) fall back to plain
 * setInterval with identical semantics.
 */

const WORKER_SOURCE = `
const timers = new Map();
self.onmessage = ({ data }) => {
  if (data.type === 'start') {
    clearInterval(timers.get(data.id));
    timers.set(data.id, setInterval(() => self.postMessage(data.id), data.ms));
  } else if (data.type === 'stop') {
    clearInterval(timers.get(data.id));
    timers.delete(data.id);
  }
};
`;

let worker = null;
let workerFailed = false;
let nextId = 1;
const callbacks = new Map();
const fallbackTimers = new Map();

const ensureWorker = () => {
  if (worker || workerFailed) return worker;
  try {
    if (typeof Worker !== 'function' || typeof Blob !== 'function' || typeof URL?.createObjectURL !== 'function') {
      throw new Error('Worker unavailable');
    }
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    worker = new Worker(url);
    URL.revokeObjectURL(url);
    worker.onmessage = ({ data: id }) => {
      const callback = callbacks.get(id);
      if (callback) callback();
    };
    worker.onerror = () => {
      // A worker blocked by CSP or a crash must not silence playback: move
      // every live timer onto the main thread.
      workerFailed = true;
      worker = null;
      callbacks.forEach((callback, id) => {
        const ms = callback.intervalMs;
        fallbackTimers.set(id, setInterval(callback, ms));
      });
    };
  } catch (_) {
    workerFailed = true;
    worker = null;
  }
  return worker;
};

export function setSchedulerInterval(callback, ms) {
  const id = nextId;
  nextId += 1;
  const tick = () => callback();
  tick.intervalMs = ms;
  callbacks.set(id, tick);
  const target = ensureWorker();
  if (target) target.postMessage({ type: 'start', id, ms });
  else fallbackTimers.set(id, setInterval(tick, ms));
  return id;
}

export function clearSchedulerInterval(id) {
  if (id === null || id === undefined) return;
  callbacks.delete(id);
  if (fallbackTimers.has(id)) {
    clearInterval(fallbackTimers.get(id));
    fallbackTimers.delete(id);
  }
  if (worker) worker.postMessage({ type: 'stop', id });
}

/** Where ticks come from: a blocked worker means the throttled main-thread timer is in use. */
export const schedulerClockMode = () => (worker ? 'worker' : workerFailed ? 'main-thread' : 'idle');
